import axios from "axios";
import { BASE_URL, API_KEY } from "../config";

/* ======================================================
   HOME LISTS (LANGUAGE SUPPORT ADDED)
   ====================================================== */

export const fetchTrendingMovies = async (language = "all") => {
  const res = await axios.get(`${BASE_URL}/trending/movie/week`, {
    params: {
      api_key: API_KEY,
      ...(language !== "all" && {
        with_original_language: language,
      }),
    },
  });

  return res.data.results || [];
};

export const fetchTrendingToday = async (language = "all") => {
  const res = await axios.get(`${BASE_URL}/trending/movie/day`, {
    params: {
      api_key: API_KEY,
      ...(language !== "all" && {
        with_original_language: language,
      }),
    },
  });

  return res.data.results || [];
};

export const fetchLatestMovies = async (language = "all") => {
  const res = await axios.get(`${BASE_URL}/movie/now_playing`, {
    params: {
      api_key: API_KEY,
      include_adult: false,
      ...(language !== "all" && {
        with_original_language: language,
      }),
    },
  });

  return res.data.results || [];
};

export const fetchTopRatedMovies = async (language = "all") => {
  const res = await axios.get(`${BASE_URL}/movie/top_rated`, {
    params: {
      api_key: API_KEY,
      include_adult: false,
      ...(language !== "all" && {
        with_original_language: language,
      }),
    },
  });

  return res.data.results || [];
};

/* ======================================================
   SEARCH & MOOD
   ====================================================== */

export const searchMovies = async (query) => {
  if (!query) return [];

  const res = await axios.get(`${BASE_URL}/search/movie`, {
    params: {
      api_key: API_KEY,
      query,
      include_adult: false,
    },
  });

  return res.data.results || [];
};

export const fetchMoviesByMood = async (genres, language = "all") => {
  const res = await axios.get(`${BASE_URL}/discover/movie`, {
    params: {
      api_key: API_KEY,
      with_genres: genres,
      sort_by: "popularity.desc",
      include_adult: false,
      ...(language !== "all" && {
        with_original_language: language,
      }),
    },
  });

  return res.data.results || [];
};

/* ======================================================
   ✅ MOVIE DETAILS (ADDED)
   ====================================================== */

export const fetchMovieDetails = async (movieId) => {
  if (!movieId) return null;

  try {
    const res = await axios.get(`${BASE_URL}/movie/${movieId}`, {
      params: { api_key: API_KEY },
    });

    return res.data || null;
  } catch (err) {
    console.error("Failed to fetch movie details:", movieId, err);
    return null;
  }
};

export const fetchMovieCredits = async (movieId) => {
  if (!movieId) return [];

  try {
    const res = await axios.get(`${BASE_URL}/movie/${movieId}/credits`, {
      params: { api_key: API_KEY },
    });

    return res.data?.cast || [];
  } catch (err) {
    console.error("Failed to fetch movie credits:", movieId, err);
    return [];
  }
};

/* ======================================================
   ✅ MOVIE WATCH PROVIDERS (ADDED)
   ====================================================== */

export const fetchWatchProviders = async (movieId) => {
  if (!movieId) return null;

  try {
    const res = await axios.get(
      `${BASE_URL}/movie/${movieId}/watch/providers`,
      { params: { api_key: API_KEY } }
    );

    const region = res.data?.results?.IN || res.data?.results?.US;

    return region || null; // returns { flatrate, rent, buy, link }
  } catch (error) {
    console.error("Watch providers fetch failed:", movieId, error);
    return null;
  }
};

/* ======================================================
   INLINE DETAILS (SAFE & CRITICAL)
   ====================================================== */

export const fetchTopActorsInline = async (movieId) => {
  if (!movieId) return [];

  try {
    const res = await axios.get(`${BASE_URL}/movie/${movieId}/credits`, {
      params: { api_key: API_KEY },
    });

    return res.data?.cast?.slice(0, 2) || [];
  } catch (err) {
    console.error("Failed to fetch actors", movieId, err);
    return [];
  }
};

export const fetchWatchProvidersInline = async (movieId) => {
  if (!movieId) return [];

  try {
    const res = await axios.get(
      `${BASE_URL}/movie/${movieId}/watch/providers`,
      { params: { api_key: API_KEY } }
    );

    const region = res.data?.results?.IN || res.data?.results?.US;

    return region?.flatrate || [];
  } catch (error) {
    console.error("OTT provider fetch failed:", movieId, error);
    return [];
  }
};

/* ======================================================
   TRAILER
   ====================================================== */

export const fetchMovieTrailer = async (movieId) => {
  if (!movieId) return null;

  try {
    const res = await axios.get(`${BASE_URL}/movie/${movieId}/videos`, {
      params: { api_key: API_KEY },
    });

    return res.data?.results?.find(
      (v) => v.site === "YouTube" && v.type === "Trailer"
    );
  } catch (err) {
    console.error("Failed to fetch trailer", movieId, err);
    return null;
  }
};

export async function fetchMovieVideos(movieId) {
  if (!movieId) return { results: [] };

  const res = await fetch(
    `${BASE_URL}/movie/${movieId}/videos?api_key=${API_KEY}&language=en-US`
  );
  return res.json();
}

/* ======================================================
   OTHER HELPERS (KEEP YOUR EXISTING)
   ====================================================== */

export const discoverByLanguageKeyword = async (language) => {
  const keywordMap = {
    te: "telugu",
    ta: "tamil",
    hi: "hindi",
    ml: "malayalam",
    kn: "kannada",
  };

  const query = keywordMap[language];
  if (!query) return [];

  const res = await axios.get(`${BASE_URL}/search/movie`, {
    params: {
      api_key: API_KEY,
      query,
      include_adult: false,
      page: 1,
    },
  });

  return res.data.results || [];
};

export const fetchPopularWebSeries = async () => {
  const res = await fetch(
    `${BASE_URL}/tv/popular?api_key=${API_KEY}&language=en-US&page=1`
  );
  const data = await res.json();
  return data.results;
};

export const fetchProviders = async (tvId) => {
  const res = await fetch(`${BASE_URL}/tv/${tvId}/watch/providers?api_key=${API_KEY}`);
  const data = await res.json();

  return (
    data.results?.IN?.flatrate?.map((p) =>
      p.provider_name.toLowerCase().replace(" ", "")
    ) || []
  );
};

export const fetchMovieNews = async () => {
  const res = await fetch(
    `${BASE_URL}/movie/upcoming?api_key=${API_KEY}&language=en-US`
  );
  const data = await res.json();
  return data.results;
};

export const fetchPopularTV = async () => {
  const res = await fetch(`${BASE_URL}/tv/popular?api_key=${API_KEY}`);
  const data = await res.json();
  return Array.isArray(data.results) ? data.results : [];
};

export const fetchTopRatedTV = async () => {
  const res = await fetch(`${BASE_URL}/tv/top_rated?api_key=${API_KEY}`);
  const data = await res.json();
  return Array.isArray(data.results) ? data.results : [];
};

export const fetchTrendingTV = async () => {
  const res = await fetch(`${BASE_URL}/trending/tv/week?api_key=${API_KEY}`);
  const data = await res.json();
  return Array.isArray(data.results) ? data.results : [];
};
